import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Opportunity,
  OpportunityLinkablePurchase,
  OpportunityModuleCustomFieldValue,
  WorkflowCustomField,
} from '../types';
import { opportunitiesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { formatApiErrorDetail } from '../utils/apiError';
import { userCanAccessPurchaseTabs } from '../constants/opportunityAccess';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { getPurchaseDisplayId, resolvePurchaseTechnicalIdFromInput } from '../utils/purchaseDisplayId';

/** Slugs reservados no backend (`opportunity_module_cf`); não mostrar como JSON ao utilizador. */
const OPP_MODULE_RESERVED_CF = new Set(['opp_default_root_folders_items', 'opp_default_root_folders_numbering']);
const MAX_ROOT_FOLDER_ROWS = 50;

type RootFolderRow = { title: string; number: string };

function buildPreviewFolderNames(rows: RootFolderRow[], numbering: boolean): string[] {
  let autoSeq = 1;
  return rows.map((row) => {
    const title = row.title.trim();
    if (!title) return '—';
    if (!numbering) return title;
    const raw = row.number.trim();
    let n: number | null = null;
    if (raw !== '') {
      const p = parseInt(raw, 10);
      if (!Number.isNaN(p) && p >= 1 && p <= 9999) n = p;
    }
    if (n !== null) {
      const label = `${n}. ${title}`;
      autoSeq = Math.max(autoSeq, n + 1);
      return label;
    }
    const label = `${autoSeq}. ${title}`;
    autoSeq += 1;
    return label;
  });
}

function parseRootFoldersFromModule(
  fields: WorkflowCustomField[],
  values: OpportunityModuleCustomFieldValue[]
): {
  rows: RootFolderRow[];
  numbering: boolean;
  itemsFieldId: number | null;
  numFieldId: number | null;
} {
  const itemsField = fields.find((f) => f.name === 'opp_default_root_folders_items');
  const numField = fields.find((f) => f.name === 'opp_default_root_folders_numbering');
  const itemsVal = values.find((v) => v.custom_field_id === itemsField?.id);
  const numVal = values.find((v) => v.custom_field_id === numField?.id);

  let rows: RootFolderRow[] = [{ title: '', number: '' }];
  if (itemsVal?.text_value?.trim()) {
    try {
      const parsed = JSON.parse(itemsVal.text_value) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        const mapped: RootFolderRow[] = parsed.map((x) => {
          const title = String((x as { title?: string }).title ?? '').trim();
          const rawNum = (x as { number?: unknown }).number;
          let number = '';
          if (rawNum !== undefined && rawNum !== null && String(rawNum).trim() !== '') {
            const p = typeof rawNum === 'number' ? rawNum : parseInt(String(rawNum), 10);
            if (!Number.isNaN(p) && p >= 1 && p <= 9999) number = String(p);
          }
          return { title, number };
        });
        rows = mapped.some((r) => r.title) ? mapped : [{ title: '', number: '' }];
      }
    } catch {
      rows = [{ title: '', number: '' }];
    }
  }

  const raw = (numVal?.text_value ?? 'true').trim().toLowerCase();
  const numbering = raw === '' || ['1', 'true', 'yes', 'on'].includes(raw);

  return {
    rows: rows.length ? rows : [{ title: '', number: '' }],
    numbering,
    itemsFieldId: itemsField?.id ?? null,
    numFieldId: numField?.id ?? null,
  };
}

const Opportunities: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const initialCreateHandled = useRef(false);
  const [items, setItems] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createPurchaseInput, setCreatePurchaseInput] = useState('');
  const [createLinkablePurchases, setCreateLinkablePurchases] = useState<OpportunityLinkablePurchase[]>([]);
  const [loadingCreateLinkablePurchases, setLoadingCreateLinkablePurchases] = useState(false);
  const [createModalError, setCreateModalError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModuleConfig, setShowModuleConfig] = useState(false);
  const [moduleWorkflowId, setModuleWorkflowId] = useState<number | null>(null);
  const [moduleCustomFields, setModuleCustomFields] = useState<WorkflowCustomField[]>([]);
  const [moduleCfValues, setModuleCfValues] = useState<OpportunityModuleCustomFieldValue[]>([]);
  const [loadingModuleConfig, setLoadingModuleConfig] = useState(false);
  const [rootFolderRows, setRootFolderRows] = useState<RootFolderRow[]>([{ title: '', number: '' }]);
  const [rootFolderNumbering, setRootFolderNumbering] = useState(true);
  const [reservedItemsFieldId, setReservedItemsFieldId] = useState<number | null>(null);
  const [reservedNumFieldId, setReservedNumFieldId] = useState<number | null>(null);
  const [savingRootFolders, setSavingRootFolders] = useState(false);
  const [deletingOpportunityId, setDeletingOpportunityId] = useState<number | null>(null);
  const [highlightOpportunityId, setHighlightOpportunityId] = useState<number | null>(null);
  const [highlightSourcePurchaseId, setHighlightSourcePurchaseId] = useState<string | null>(null);
  const rootFolderFormDirtyRef = useRef(false);

  const canEdit = userCanAccessPurchaseTabs(user);

  const load = useCallback(async () => {
    if (!canEdit) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await opportunitiesApi.list(search.trim() || undefined);
      setItems(data);
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao carregar oportunidades.'));
    } finally {
      setLoading(false);
    }
  }, [canEdit, search]);

  useEffect(() => {
    load();
  }, [load]);

  const loadCreateLinkablePurchases = useCallback(
    async (searchTerm?: string) => {
      if (!canEdit) return;
      setLoadingCreateLinkablePurchases(true);
      try {
        const items = await opportunitiesApi.listLinkablePurchasesForCreate(searchTerm?.trim() || undefined);
        setCreateLinkablePurchases(items);
      } catch (err: any) {
        setCreateModalError(
          formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao carregar requisições para vínculo.')
        );
      } finally {
        setLoadingCreateLinkablePurchases(false);
      }
    },
    [canEdit]
  );

  useEffect(() => {
    if (!canEdit || initialCreateHandled.current) return;
    const state = location.state as {
      openCreate?: boolean;
      sourcePurchaseId?: number;
      sourcePurchasePublicId?: string;
      suggestedTitle?: string;
    } | null;
    if (!state?.openCreate) return;

    initialCreateHandled.current = true;
    const openFromPurchaseContext = async () => {
      const sourcePurchaseId = Number(state.sourcePurchaseId);
      const sourcePurchasePublicId = String(state.sourcePurchasePublicId || '').trim();
      const sourcePurchaseDisplayId =
        sourcePurchasePublicId ||
        (Number.isFinite(sourcePurchaseId) && sourcePurchaseId > 0 ? String(sourcePurchaseId) : '');
      setCreateModalError('');
      setHighlightOpportunityId(null);
      setHighlightSourcePurchaseId(null);

      if (Number.isFinite(sourcePurchaseId) && sourcePurchaseId > 0) {
        try {
          const existing = await opportunitiesApi.list();
          const existingLinked = existing.find(
            (opportunity) => Number(opportunity.purchase_request_id) === sourcePurchaseId
          );
          if (existingLinked) {
            setItems(existing);
            setHighlightOpportunityId(existingLinked.id);
            setHighlightSourcePurchaseId(
              existingLinked.purchase_request?.public_id || sourcePurchaseDisplayId || String(sourcePurchaseId)
            );
            navigate(location.pathname, { replace: true });
            return;
          }
        } catch {
          // Falha na busca não deve bloquear o fluxo de criação.
        }
      }

      setShowCreate(true);
      void loadCreateLinkablePurchases('');
      setNewTitle(state.suggestedTitle || '');
      if (sourcePurchaseDisplayId) {
        setNewDescription(`Criada a partir da requisição #${sourcePurchaseDisplayId}.`);
        setCreatePurchaseInput(`#${sourcePurchaseDisplayId}`);
      }
      navigate(location.pathname, { replace: true });
    };

    void openFromPurchaseContext();
  }, [canEdit, loadCreateLinkablePurchases, location.pathname, location.state, navigate]);

  const createOpportunity = async () => {
    if (!newTitle.trim()) {
      setCreateModalError('Informe o título da oportunidade.');
      return;
    }
    setSaving(true);
    setCreateModalError('');
    try {
      const normalizedPurchaseInput = createPurchaseInput.trim();
      let purchaseRequestId: number | undefined;
      let purchasePublicId: string | undefined;
      if (normalizedPurchaseInput) {
        const idMatch = normalizedPurchaseInput.match(/^#?\s*(\d+(?:\.\d+)?)\b/);
        if (!idMatch) {
          setCreateModalError(
            'Para vincular, digite e selecione uma requisição pelo número (ex.: #10001 ou #10001.1).'
          );
          setSaving(false);
          return;
        }
        purchasePublicId = idMatch[1];
        const resolvedId = resolvePurchaseTechnicalIdFromInput(normalizedPurchaseInput, createLinkablePurchases);
        if (resolvedId !== null) {
          purchaseRequestId = resolvedId;
        }
      }

      await opportunitiesApi.create({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
        purchase_request_id: purchaseRequestId,
        purchase_public_id: purchasePublicId,
      });
      setShowCreate(false);
      setNewTitle('');
      setNewDescription('');
      setCreatePurchaseInput('');
      setCreateModalError('');
      await load();
    } catch (err: any) {
      setCreateModalError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao criar oportunidade.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteOpportunity = async (item: Opportunity) => {
    const step1 = await showConfirm({
      title: 'Excluir oportunidade',
      message: `Deseja excluir a oportunidade «${item.title}»?`,
      details:
        'Todas as pastas, subpastas e arquivos associados serão removidos permanentemente. Esta ação não pode ser desfeita.',
      variant: 'warning',
      confirmLabel: 'Continuar',
      cancelLabel: 'Cancelar',
    });
    if (!step1) return;

    const step2 = await showConfirm({
      title: 'Confirmação final',
      message:
        'Está prestes a eliminar permanentemente esta oportunidade e todo o conteúdo associado (incluindo arquivos no armazenamento).',
      details:
        'Em produção isto pode afetar auditoria e documentos com valor legal. Só prossiga se tiver certeza absoluta.',
      variant: 'danger',
      confirmLabel: 'Excluir definitivamente',
      cancelLabel: 'Voltar',
    });
    if (!step2) return;

    setDeletingOpportunityId(item.id);
    setError('');
    try {
      await opportunitiesApi.delete(item.id);
      showAlert({
        title: 'Oportunidade excluída',
        message: `A oportunidade «${item.title}» foi removida.`,
        variant: 'success',
      });
      await load();
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao excluir oportunidade.'));
    } finally {
      setDeletingOpportunityId(null);
    }
  };

  const openModuleConfigModal = async () => {
    if (!canEdit) return;
    rootFolderFormDirtyRef.current = false;
    setShowModuleConfig(true);
    setLoadingModuleConfig(true);
    setError('');
    try {
      const meta = await opportunitiesApi.getModuleWorkflowMeta();
      const [fields, values] = await Promise.all([
        opportunitiesApi.getModuleCustomFieldsForStep(),
        opportunitiesApi.getModuleCustomFieldValues(),
      ]);
      setModuleWorkflowId(meta.workflow_id);
      setModuleCustomFields(fields || []);
      setModuleCfValues(values || []);
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao carregar configuração do módulo.'));
      setShowModuleConfig(false);
    } finally {
      setLoadingModuleConfig(false);
    }
  };

  const reloadModuleConfig = async () => {
    if (!canEdit || moduleWorkflowId == null) return;
    try {
      const [fields, values] = await Promise.all([
        opportunitiesApi.getModuleCustomFieldsForStep(),
        opportunitiesApi.getModuleCustomFieldValues(),
      ]);
      setModuleCustomFields(fields || []);
      setModuleCfValues(values || []);
    } catch {
      /* noop: erro já tratado no editor */
    }
  };

  useEffect(() => {
    if (!showModuleConfig || loadingModuleConfig || moduleCustomFields.length === 0) return;
    if (rootFolderFormDirtyRef.current) return;
    const parsed = parseRootFoldersFromModule(moduleCustomFields, moduleCfValues);
    setRootFolderRows(parsed.rows);
    setRootFolderNumbering(parsed.numbering);
    setReservedItemsFieldId(parsed.itemsFieldId);
    setReservedNumFieldId(parsed.numFieldId);
  }, [showModuleConfig, loadingModuleConfig, moduleCustomFields, moduleCfValues]);

  const saveRootFolders = async () => {
    if (reservedItemsFieldId == null || reservedNumFieldId == null) {
      setError('Campos reservados do módulo não encontrados. Contacte o administrador.');
      return;
    }
    const cleanedRows = rootFolderRows.filter((x) => x.title.trim());
    if (cleanedRows.length > MAX_ROOT_FOLDER_ROWS) {
      setError(`No máximo ${MAX_ROOT_FOLDER_ROWS} pastas na lista.`);
      return;
    }
    const lower = cleanedRows.map((x) => x.title.trim().toLowerCase());
    if (new Set(lower).size !== lower.length) {
      setError('Não são permitidos títulos repetidos (ignorando maiúsculas/minúsculas).');
      return;
    }
    const explicitNums = cleanedRows
      .map((r) => r.number.trim())
      .filter((s) => s !== '')
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n) && n >= 1 && n <= 9999);
    if (new Set(explicitNums).size !== explicitNums.length) {
      setError('Não são permitidos números explícitos repetidos na coluna N.º.');
      return;
    }
    setSavingRootFolders(true);
    setError('');
    try {
      const itemsJson = JSON.stringify(
        cleanedRows.map((row) => {
          const o: { title: string; number?: number } = { title: row.title.trim() };
          const r = row.number.trim();
          if (r !== '') {
            const n = parseInt(r, 10);
            if (!Number.isNaN(n) && n >= 1 && n <= 9999) o.number = n;
          }
          return o;
        })
      );
      await opportunitiesApi.putModuleCustomFieldValue(reservedItemsFieldId, { text_value: itemsJson });
      await opportunitiesApi.putModuleCustomFieldValue(reservedNumFieldId, {
        text_value: rootFolderNumbering ? 'true' : 'false',
      });
      rootFolderFormDirtyRef.current = false;
      await reloadModuleConfig();
      showAlert({
        title: 'Pastas na raiz',
        message: 'Configuração guardada. Novas oportunidades usarão esta lista.',
        variant: 'success',
      });
    } catch (err: any) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, 'Falha ao guardar pastas na raiz.'));
    } finally {
      setSavingRootFolders(false);
    }
  };

  const editorModuleFields = moduleCustomFields.filter((f) => !OPP_MODULE_RESERVED_CF.has(f.name));
  const editorModuleValues = moduleCfValues.filter((v) => {
    const n = v.custom_field?.name;
    return n && !OPP_MODULE_RESERVED_CF.has(n);
  });

  const previewFolderNames = useMemo(
    () => buildPreviewFolderNames(rootFolderRows, rootFolderNumbering),
    [rootFolderRows, rootFolderNumbering]
  );

  return (
    <div className="p-3">
      {!canEdit && (
        <Alert variant="warning">
          Você não possui permissão para acessar oportunidades de contratação.
        </Alert>
      )}
      {canEdit && (
      <Card>
        <Card.Body>
          <Row className="align-items-center mb-3 g-2">
            <Col xs={12} md={7}>
              <h4 className="mb-0">Oportunidades de Contratação</h4>
            </Col>
            <Col xs={12} md={5} className="d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
              <Form.Control
                placeholder="Buscar oportunidade"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    load();
                  }
                }}
              />
              <Button variant="outline-secondary" onClick={load}>
                Buscar
              </Button>
              {canEdit && (
                <Button variant="outline-secondary" onClick={() => void openModuleConfigModal()}>
                  Configurar pastas padrão
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setNewTitle('');
                    setNewDescription('');
                    setCreatePurchaseInput('');
                    setCreateModalError('');
                    setShowCreate(true);
                    void loadCreateLinkablePurchases('');
                  }}
                >
                  Nova
                </Button>
              )}
            </Col>
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}
          {!error && highlightOpportunityId && highlightSourcePurchaseId && (
            <Alert variant="info" className="d-flex justify-content-between align-items-center">
              <span>
                Oportunidade da requisição #{highlightSourcePurchaseId} destacada na lista.
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setHighlightOpportunityId(null);
                  setHighlightSourcePurchaseId(null);
                }}
              >
                Limpar destaque
              </Button>
            </Alert>
          )}
          {loading ? (
            <div>Carregando...</div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Status</th>
                    <th>Atualizada em</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        Nenhuma oportunidade encontrada.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr
                        key={item.id}
                        className={item.id === highlightOpportunityId ? 'table-primary' : undefined}
                      >
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span>{item.title}</span>
                            {item.id === highlightOpportunityId && (
                              <span className="badge bg-primary">Destacada</span>
                            )}
                          </div>
                        </td>
                        <td>{item.status}</td>
                        <td>{new Date(item.updated_at).toLocaleString('pt-BR')}</td>
                        <td className="text-end">
                          <div className="d-inline-flex flex-wrap gap-2 justify-content-end">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={deletingOpportunityId === item.id}
                              onClick={() => navigate(`/purchases/opportunities/${item.id}`)}
                            >
                              Abrir
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={deletingOpportunityId === item.id}
                              onClick={() => void confirmDeleteOpportunity(item)}
                            >
                              {deletingOpportunityId === item.id ? 'A excluir…' : 'Excluir'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      )}

      <Modal show={showModuleConfig} onHide={() => setShowModuleConfig(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Configurar pastas padrão das oportunidades</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingModuleConfig || moduleWorkflowId == null ? (
            <div>Carregando…</div>
          ) : (
            <>
              <h6 className="mb-2">Pastas na raiz de novas oportunidades</h6>
              <Alert variant="info" className="small py-2 mb-3">
                <strong>Oportunidades que já existem</strong> não são atualizadas automaticamente quando altera esta
                lista. Para criar estas pastas dentro de uma oportunidade (raiz ou outra pasta), abra a oportunidade e
                use <strong>Aplicar conjunto padrão</strong> na árvore de pastas. A sexta pasta (e as seguintes) só
                aparecem depois de <strong>guardar aqui</strong> e voltar a aplicar onde precisar.
              </Alert>
              <p className="text-muted small mb-3">
                Com a numeração ligada, deixe <strong>N.º</strong> vazio para 1., 2., 3.… em sequência, ou preencha um
                número (ex.: 6) para forçar <strong>«6. título»</strong>. A coluna <strong>Nome gerado</strong> mostra o
                resultado antes de guardar.
              </p>
              <Form.Check
                type="switch"
                id="opp-root-numbering"
                className="mb-3"
                label='Numeração nos nomes (ex.: «1. Planejamento…» ou «6. …» quando define o N.º)'
                checked={rootFolderNumbering}
                onChange={(e) => {
                  rootFolderFormDirtyRef.current = true;
                  setRootFolderNumbering(e.target.checked);
                }}
                disabled={!canEdit || savingRootFolders}
              />
              <Row className="text-muted small fw-semibold gx-2 mb-1 d-none d-md-flex">
                <Col style={{ maxWidth: 88 }}>N.º</Col>
                <Col>Título</Col>
                <Col md={4}>Nome gerado</Col>
                <Col style={{ width: 90 }} />
              </Row>
              <div className="d-flex flex-column gap-2 mb-2">
                {rootFolderRows.map((row, idx) => (
                  <Row className="g-2 align-items-center" key={idx}>
                    <Col xs={4} md="auto" style={{ maxWidth: 100 }}>
                      <Form.Control
                        type="text"
                        inputMode="numeric"
                        placeholder="auto"
                        value={row.number}
                        maxLength={4}
                        disabled={!canEdit || savingRootFolders}
                        onChange={(e) => {
                          rootFolderFormDirtyRef.current = true;
                          const next = [...rootFolderRows];
                          next[idx] = { ...next[idx], number: e.target.value.replace(/\D/g, '') };
                          setRootFolderRows(next);
                        }}
                      />
                    </Col>
                    <Col xs={8} md>
                      <Form.Control
                        placeholder={`Título ${idx + 1}`}
                        value={row.title}
                        maxLength={255}
                        disabled={!canEdit || savingRootFolders}
                        onChange={(e) => {
                          rootFolderFormDirtyRef.current = true;
                          const next = [...rootFolderRows];
                          next[idx] = { ...next[idx], title: e.target.value };
                          setRootFolderRows(next);
                        }}
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <span className="text-muted small text-break d-block">{previewFolderNames[idx] ?? '—'}</span>
                    </Col>
                    <Col xs="auto">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={!canEdit || savingRootFolders || rootFolderRows.length <= 1}
                        onClick={() => {
                          rootFolderFormDirtyRef.current = true;
                          setRootFolderRows((prev) => prev.filter((_, i) => i !== idx));
                        }}
                      >
                        Remover
                      </Button>
                    </Col>
                  </Row>
                ))}
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                className="mb-4"
                disabled={!canEdit || savingRootFolders || rootFolderRows.length >= MAX_ROOT_FOLDER_ROWS}
                onClick={() => {
                  rootFolderFormDirtyRef.current = true;
                  setRootFolderRows((prev) => [...prev, { title: '', number: '' }]);
                }}
              >
                Adicionar pasta
              </Button>
              <div className="d-flex flex-wrap gap-2 mb-4">
                <Button variant="primary" size="sm" disabled={!canEdit || savingRootFolders} onClick={() => void saveRootFolders()}>
                  {savingRootFolders ? 'A guardar…' : 'Guardar pastas na raiz'}
                </Button>
              </div>

              {editorModuleFields.length > 0 && (
                <>
                  <hr className="my-3" />
                  <h6 className="mb-2">Outros campos do módulo</h6>
                  <p className="text-muted small mb-3">
                    Campos extra do workflow interno (apenas tipos permitidos pelo sistema).
                  </p>
                  <CustomFieldsEditor
                    ticketId={0}
                    workflowId={moduleWorkflowId}
                    customFields={editorModuleFields}
                    customFieldValues={editorModuleValues}
                    onSave={() => void reloadModuleConfig()}
                    canEdit={canEdit}
                    areaType="opportunities-module"
                  />
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModuleConfig(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Nova oportunidade</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createModalError && <Alert variant="danger" className="py-2">{createModalError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Título</Form.Label>
            <Form.Control
              value={newTitle}
              maxLength={500}
              onChange={(event) => setNewTitle(event.target.value)}
              placeholder="Ex.: Contratação serviço X"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newDescription}
              onChange={(event) => setNewDescription(event.target.value)}
              placeholder="Contexto da oportunidade"
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Vincular à requisição (opcional)</Form.Label>
            <Form.Control
              list="create-linkable-purchases-list"
              value={createPurchaseInput}
              onChange={(event) => {
                const next = event.target.value;
                setCreatePurchaseInput(next);
                void loadCreateLinkablePurchases(next);
              }}
              placeholder="Digite o número ou título (ex.: #10001 ou #10001.1)"
            />
            <datalist id="create-linkable-purchases-list">
              {createLinkablePurchases.map((purchase) => (
                <option
                  key={`create-link-pr-${purchase.id}`}
                  value={`#${getPurchaseDisplayId(purchase)} - ${purchase.title}`}
                />
              ))}
            </datalist>
            <Form.Text className="text-muted">
              Campo único com busca. Se ficar vazio, cria sem vínculo.
              {loadingCreateLinkablePurchases ? ' Buscando...' : ''}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowCreate(false);
              setCreatePurchaseInput('');
              setCreateModalError('');
            }}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={createOpportunity} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Opportunities;
